import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Loader2, Trash2 } from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import { adminApi, TOKEN_KEY } from "@/admin/adminApi";
import { toast } from "@/admin/Toast";

const STATUSES = ["new", "reviewing", "responded", "closed"];
const REQUEST_TYPES = [
  ["", "All types"],
  ["inspection-service", "Inspection Service"],
  ["quality-assurance-quality-control", "QA / QC"],
  ["conformity-support", "Conformity Support"],
  ["logistics-management", "Logistics Management"],
  ["supply-chain-solution", "Supply Chain Solution"],
  ["medical-laboratory-equipment", "Medical Laboratory Equipment"],
  ["general-request", "General Request"],
];

function StatusBadge({ status }) {
  const cls =
    status === "new" ? "bg-mist-gold/20 text-mist-navy" :
    status === "reviewing" ? "bg-blue-100 text-blue-800" :
    status === "responded" ? "bg-emerald-100 text-emerald-800" :
    "bg-mist-ink/10 text-mist-ink/60";
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${cls}`}>{status}</span>;
}

function EmailStatusBadge({ status }) {
  const s = status || "unknown";
  const label =
    s === "sent" ? "sent" :
    s === "failed" ? "failed" :
    s === "skipped_no_provider" ? "held (no email)" :
    s === "pending" ? "pending" : s;
  const cls =
    s === "sent" ? "bg-emerald-100 text-emerald-800" :
    s === "failed" ? "bg-red-100 text-red-700" :
    s === "skipped_no_provider" ? "bg-amber-100 text-amber-800" :
    "bg-mist-ink/10 text-mist-ink/60";
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${cls}`} title={status || ""}>{label}</span>;
}

export default function QuoteRequestsPage() {
  const [data, setData] = useState({ items: null, total: 0, new_count: 0 });
  const [status, setStatus] = useState("");
  const [requestType, setRequestType] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const reload = useCallback(async () => {
    const params = {};
    if (status) params.status = status;
    if (requestType) params.request_type = requestType;
    if (q) params.q = q;
    const { data } = await adminApi.get("/quote-requests", { params });
    setData(data);
  }, [status, requestType, q]);

  useEffect(() => {
    reload();
  }, [reload]);

  const setSubStatus = async (id, next) => {
    try {
      await adminApi.patch(`/quote-requests/${id}`, { status: next });
      toast("success", `Status → ${next}`);
      if (detail && detail.id === id) setDetail((d) => ({ ...d, status: next }));
      reload();
    } catch {
      toast("error", "Update failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this request? This also removes its attachment.")) return;
    try {
      await adminApi.delete(`/quote-requests/${id}`);
      toast("success", "Deleted");
      setDetail(null);
      reload();
    } catch {
      toast("error", "Delete failed");
    }
  };

  const downloadAttachment = async (id, filename) => {
    try {
      const res = await adminApi.get(`/quote-requests/${id}/attachment`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "attachment"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast("error", "Download failed");
    }
  };

  const items = data.items;

  return (
    <AdminLayout title="Quote requests">
      <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="quote-admin-toolbar">
        <div className="text-sm text-mist-navy/70">
          <span className="font-semibold text-mist-navy">{data.new_count}</span> new / {data.total} total
        </div>
        <div className="flex-1" />
        <input
          type="text"
          placeholder="Search name / email / subject…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-9 px-3 border border-mist-soft rounded-md text-sm w-64 focus:outline-none focus:border-mist-gold"
          data-testid="quote-search"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 px-3 border border-mist-soft rounded-md text-sm" data-testid="quote-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={requestType} onChange={(e) => setRequestType(e.target.value)} className="h-9 px-3 border border-mist-soft rounded-md text-sm" data-testid="quote-type-filter">
          {REQUEST_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {!items ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-mist-navy" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-mist-soft rounded-md p-10 text-center text-sm text-mist-ink/60">No quote requests.</div>
      ) : (
        <div className="bg-white border border-mist-soft rounded-md overflow-hidden" data-testid="quote-admin-list">
          <table className="w-full text-sm">
            <thead className="bg-mist-off text-[11px] uppercase tracking-[0.22em] text-mist-navy/60">
              <tr>
                <th className="px-4 py-3 text-left">Received</th>
                <th className="px-4 py-3 text-left">Full name</th>
                <th className="px-4 py-3 text-left">Company</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Request type</th>
                <th className="px-4 py-3 text-left">Subject</th>
                <th className="px-4 py-3 text-left">Email delivery</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-mist-soft/70 hover:bg-mist-off/50 cursor-pointer"
                  onClick={() => { setDetail(r); if (r.status === "new") setSubStatus(r.id, "reviewing"); }}
                  data-testid={`quote-row-${r.id}`}
                >
                  <td className="px-4 py-3 text-xs text-mist-ink/60">{(r.submitted_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">{r.full_name}</td>
                  <td className="px-4 py-3 text-mist-ink/70">{r.company || "—"}</td>
                  <td className="px-4 py-3 text-mist-navy">{r.email}</td>
                  <td className="px-4 py-3 text-xs">{r.request_type}</td>
                  <td className="px-4 py-3 truncate max-w-xs">{r.subject}</td>
                  <td className="px-4 py-3"><EmailStatusBadge status={r.admin_email_status} /></td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-mist-navy/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-md p-6 max-w-3xl w-full shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="quote-detail">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-xl text-mist-navy">{detail.subject}</h3>
                <div className="text-xs text-mist-ink/60 mt-1">{detail.request_type}</div>
              </div>
              <button onClick={() => setDetail(null)} className="text-mist-ink/60 hover:text-mist-navy text-2xl">×</button>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
              <div><span className="text-mist-ink/60 text-xs uppercase tracking-wide">Full name</span><div className="text-mist-navy font-medium">{detail.full_name}</div></div>
              <div><span className="text-mist-ink/60 text-xs uppercase tracking-wide">Company</span><div className="text-mist-navy">{detail.company || "—"}</div></div>
              <div><span className="text-mist-ink/60 text-xs uppercase tracking-wide">Email</span><div><a href={`mailto:${detail.email}`} className="text-mist-navy hover:text-mist-gold">{detail.email}</a></div></div>
              <div><span className="text-mist-ink/60 text-xs uppercase tracking-wide">Phone</span><div className="text-mist-navy">{detail.phone}</div></div>
              <div><span className="text-mist-ink/60 text-xs uppercase tracking-wide">Country</span><div className="text-mist-navy">{detail.country}</div></div>
              <div><span className="text-mist-ink/60 text-xs uppercase tracking-wide">Service</span><div className="text-mist-navy">{detail.service || "—"}</div></div>
              <div className="col-span-2"><span className="text-mist-ink/60 text-xs uppercase tracking-wide">Product</span><div className="text-mist-navy">{detail.product || "—"}{detail.product_other ? ` (${detail.product_other})` : ""}</div></div>
            </div>
            <div className="text-sm text-mist-ink whitespace-pre-wrap bg-mist-off border border-mist-soft rounded-md p-4 mb-4">{detail.message}</div>
            {detail.attachment && (
              <div className="mb-4 flex items-center gap-3 text-sm">
                <span className="text-mist-ink/60 text-xs uppercase tracking-wide">Attachment:</span>
                <button
                  type="button"
                  onClick={() => downloadAttachment(detail.id, detail.attachment.filename)}
                  data-testid="quote-download-attachment"
                  className="inline-flex items-center gap-2 px-3 h-9 bg-white border border-mist-soft rounded-md hover:border-mist-gold text-sm"
                >
                  <Download className="h-4 w-4" />
                  {detail.attachment.filename} <span className="text-mist-ink/50">({Math.round((detail.attachment.size || 0) / 1024)} KB)</span>
                </button>
              </div>
            )}
            <div className="text-xs text-mist-ink/60 mb-4">
              Received {detail.submitted_at} · IP {detail.ip}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs uppercase tracking-widest text-mist-ink/60">Status:</label>
              <select
                value={detail.status}
                onChange={(e) => setSubStatus(detail.id, e.target.value)}
                className="h-9 px-3 border border-mist-soft rounded-md text-sm"
                data-testid="quote-status-select"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                onClick={() => del(detail.id)}
                data-testid="quote-delete"
                className="ml-auto text-xs uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 px-4 h-9 rounded-full hover:bg-red-100 inline-flex items-center gap-1.5"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
